import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { firstValueFrom } from 'rxjs';
import { CommonServiceService } from 'src/app/Services/common-service.service';
import { onUserLogout } from 'src/app/Services/datadog-session';
import { UserserviceService } from 'src/app/Services/userservice.service';
import { LicenseStatus } from 'src/app/enum/enum';
import { environment } from 'src/app/environments/environment';

import {
  LoginModel,
  MainDepartment,
  ProductionMetadata,
  Stations,
  Stores,
  SubDepartmentModel,
  SubDepartments,
  TpmLicenseModel,
  UserFavoriteModal,
  UserFavoriteRequestModel,
  UsersModelForEmail
} from '../../../Models/ApplicationModels';
import { BreadcrumbService } from '../../../Services/breadcrumb.service';
import { CustomToastsService } from '../../../Services/custom-toasts.service';
import { FavouritesStateService } from '../../../Services/favourites-state.service';
import { GoogleTranslateService } from '../../../Services/google-translate.service';
import { MasterDataSeriveService } from '../../../Services/master-data-serive.service';
import { DynamicTranslationService } from '../../../dynamic-translation.service';
import { TranslationCommunicationServiceService } from '../../../translation-communication-service.service';
import { TranslationService } from '../../../translation.service';

@Component({
  selector: 'app-departments',
  templateUrl: './departments.component.html',
  styleUrls: ['./departments.component.css']
})
export class DepartmentsComponent {
  @ViewChild('stationWarning') stationWarning?: ElementRef<HTMLElement>;

  mainDepartmentList: Array<MainDepartment> = [];
  mainDepartmentListforOriginal: Array<MainDepartment> = [];
  subDepartmentList: Array<SubDepartments> = [];
  isButtonActive: boolean = false;
  urlName: string = '';
  inputText: string = 'school';
  translatedTexts: string[] = [];
  departmentDescrListinEnglish: any[] = [];
  departmentDescrCombinedListinEnglish: any[] = [];
  selectedLanguage: string = 'en';
  mergedFavoritesList: any[] = [];
  renderWomensDepartmentDat: Array<SubDepartmentModel> = [];
  renderWomensDepartmentDataForOriginal: Array<SubDepartmentModel> = [];
  subDepartmentDescrListinEnglish: any[] = [];
  showFav: boolean = true;
  descriptionsToTranslate: any[] = [];
  ctgName: string = '';
  isNavigatingBack: any = '';
  favColor: string = '';
  userFavoriteModal: UserFavoriteModal = new UserFavoriteModal();
  combinedItemsList: Array<UserFavoriteModal> = [];
  itemDescription: string = '';
  allStations: Array<Stations> = [];
  favQuantity: any = '00';
  formMasters: string[] = [];
  userInfo: LoginModel = new LoginModel();
  stationslist: Array<Stations> = [];
  isLimitReached: boolean = false;
  deptName: string = '';
  userInfoEmail: UsersModelForEmail = new UsersModelForEmail();
  licenseList: Array<TpmLicenseModel> = [];
  updatingFavoriteDeptId: number | null = null;
  defaultStoreName: string = '';

  constructor(
    private route: Router,
    private masterService: MasterDataSeriveService,
    public translationService: TranslationService,
    private dynamicTranslationService: DynamicTranslationService,
    private translationCommunicationService: TranslationCommunicationServiceService,
    private breadcrumbService: BreadcrumbService,
    private toast: CustomToastsService,
    private userservice: UserserviceService,
    private commonService: CommonServiceService,
    private googleTranslateService: GoogleTranslateService,
    private favouritesState: FavouritesStateService
  ) {}

  async ngOnInit() {
    this.toast.error('');
    this.toast.success('');
    this.userInfo = this.masterService.getUserDetailsFromStorage();
    this.breadcrumbService.updateActiveCategoryTab('');

    this.getStoresList();

    const isUserFirstVisit = sessionStorage.getItem('isFirstVisit') === 'true';
    const isBypassLicense = sessionStorage.getItem('isBypassLicense') === 'true';

    if (isUserFirstVisit && !isBypassLicense) {
      await this.getLicenses();
      await this.getStationslist();
    }

    try {
      await this.assignUserStation();
    } catch (e) {
      console.error(`Error : ${e}`);
    }

    const hasFav = this.favouritesState.getFlag();
    if (hasFav === false) {
      this.isButtonActive = true;
      this.initializeData();
      this.getMainDepartments();
    } else {
      await this.getAllFavItems();
      if (!this.isButtonActive) {
        this.initializeData();
      }
    }

    this.translationCommunicationService.getSelectedLanguage().subscribe(language => {
      this.selectedLanguage = language;
    });

    // Clear the flag from localStorage
    this.isNavigatingBack = false;
    localStorage.removeItem('isNavigatingBack');
    await this.productionMetadata();
  }

  async productionMetadata() {
    await this.commonService.getWeekColorCycle();
    const productionMetadata = new ProductionMetadata();
    let productionCycle = JSON.parse(localStorage.getItem('productionCycle') || '[]');

    const cycleData = Array.isArray(productionCycle) ? productionCycle[0] : productionCycle;

    const isDateSpecific =
      (this.userInfo?.productionScheduleType ?? '').toString().trim().toLowerCase() === 'datespecific';

    const activeStoreId = Number(localStorage.getItem('tempStoreId') || localStorage.getItem('storeId') || '0');

    productionMetadata.companyId = this.userInfo?.companyID;
    productionMetadata.userId = this.userInfo?.userId;
    productionMetadata.storeId = this.userInfo?.storeId;

    if (isDateSpecific) {
      // datespecific companies have no production cycle — send 0
      productionMetadata.productionCycleId = 0;
    } else if (cycleData?.productionCycleId) {
      productionMetadata.productionCycleId = parseInt(cycleData.productionCycleId);
    } else {
      // No cycle data yet, skip
      return;
    }

    try {
      const res = await firstValueFrom(this.masterService.createProductionMetadata(productionMetadata));
      localStorage.setItem('productionMetadata', JSON.stringify(res));
    } catch (e) {
      console.error('Failed to create productionMetadata:', e);
    }
  }

  async getLicenses() {
    try {
      const companyId = this.userInfo?.companyID;
      const res = await firstValueFrom(this.masterService.getAvailableLicenses(companyId));
      this.licenseList = (res as TpmLicenseModel[]) || [];
    } catch (e) {
      console.error('Error fetching licenses:', e);
    }
  }

  async getStationslist() {
    try {
      const userdata = this.masterService.getUserDetailsFromStorage();
      const companyId = userdata?.companyID;
      const currentStoreId = Number(localStorage.getItem('tempStoreId') || localStorage.getItem('storeId') || '0');

      const result = await firstValueFrom(this.masterService.getStationslist(companyId));
      let stations = (result as Stations[]) || [];
      this.allStations = stations;

      this.stationslist = stations.filter(
        station =>
          station.storeId == currentStoreId &&
          station?.active === true &&
          !(this.licenseList || []).some(license => license?.currentlyAssignedToId === station?.stationId)
      );
    } catch (e) {
      console.error('Error fetching stations list:', e);
    }
  }

  async getStoresList() {
    let storeName = localStorage.getItem('defaultStoreName') || '';
    if (storeName) this.defaultStoreName = storeName;
  }

  private async initializeData(): Promise<void> {
    if (this.isButtonActive) {
      this.breadcrumbService.updateActiveDeptTab('Choose Department');
    } else {
      this.breadcrumbService.updateActiveDeptTab('Favorites');
    }
  }

  //Translation logic:
  updateMainDepartmentContent(): void {
    this.dynamicTranslationService
      .translateText(this.departmentDescrListinEnglish)
      .subscribe((translatedDescriptions: string[]) => {
        this.mainDepartmentList.forEach((dept, index) => {
          dept.departmentDescr = translatedDescriptions[index];
        });
      });
  }

  translateText(inputText: any) {
    this.dynamicTranslationService.translateText(inputText).subscribe((translatedTexts: string[]) => {
      this.updateMainDepartmentContent();
    });
  }

  async assignUserStation(): Promise<void> {
    const user = this.masterService.getUserDetailsFromStorage();
    const isBypassLicense = sessionStorage.getItem('isBypassLicense') === 'true';
    const isUserFirstVisit = sessionStorage.getItem('isFirstVisit') === 'true';
    const stationId = this.stationslist?.[0]?.stationId;
    const isLicenseAvailable = this.licenseList.find(license => license.status === LicenseStatus.Available);

    if (isBypassLicense) {
      if (isUserFirstVisit) {
        this.toast.success('Bypass license enabled');
        sessionStorage.setItem('isFirstVisit', 'false');
      }
      return;
    }

    if (!isUserFirstVisit) {
      return;
    }

    // Check if the current user is ALREADY assigned a station license in DB
    const existingUserLicense = (this.licenseList || []).find(
      license => license?.currentlyAssignedToUserId === Number(user?.userId)
    );

    if (existingUserLicense && existingUserLicense.currentlyAssignedToId) {
      const assignedStationId = existingUserLicense.currentlyAssignedToId;
      localStorage.setItem('stationId', JSON.stringify(assignedStationId));
      let stationsName = (this.allStations || []).find(station => station.stationId == assignedStationId)?.stationName;
      localStorage.setItem('stationName', stationsName || '');
      this.commonService.setStation(stationsName || '');
      await this.createStationSession(assignedStationId, existingUserLicense.id, user.companyID);
      sessionStorage.setItem('isFirstVisit', 'false');
      return;
    }

    if (!user || !isLicenseAvailable || !stationId) {
      this.handleStationLimitReached();
      return;
    }

    const assignRequest = {
      assignedToUserId: Number(user.userId),
      assignedToId: Number(stationId)
    };

    localStorage.setItem('stationId', JSON.stringify(stationId));

    try {
      const res: any = await firstValueFrom(this.masterService.assignStationLicense(assignRequest, user.companyID));
      let stationsName = this.stationslist.find(station => station.stationId == stationId)?.stationName;
      localStorage.setItem('stationName', stationsName || '');
      this.commonService.setStation(stationsName);
      await this.createStationSession(stationId, res?.license?.id, user.companyID);
      this.toast.success(res.message);
      sessionStorage.setItem('isFirstVisit', 'false');
    } catch (error) {
      this.toast.error('Failed to assign station license:');
    }
  }

  // AB#13334: mints the TPM-side session the heartbeat/release flow depends
  // on. Called with the caller's own token (Interceptor attaches it) right
  // after CompanyMgmt confirms the license assignment - never on its own.
  private async createStationSession(stationId: number, licenseId: number, companyId: number): Promise<void> {
    if (!stationId || !licenseId || !companyId) {
      return;
    }

    try {
      const res = await firstValueFrom(
        this.masterService.createStationSession({
          stationId: Number(stationId),
          companyId: Number(companyId),
          licenseId: Number(licenseId)
        })
      );
      if (res?.sessionId) {
        localStorage.setItem('stationSessionId', res.sessionId);
      }
    } catch (error) {
      console.error('Failed to create station session:', error);
    }
  }

  private handleStationLimitReached(): void {
    this.isLimitReached = true;
    this.stationWarning?.nativeElement.click();
  }

  getdeptId() {
    let dept: any = JSON.parse(localStorage.getItem('deptID') || '{}');
    let User: any = localStorage.getItem('user');
    let userDetails = JSON.parse(User);
    this.getDepartmentsCategoryData(dept.departmentId, userDetails[0].companyID);
  }

  toggleButton1() {
    this.isButtonActive = false;
    localStorage.removeItem('isNavigatingBack');
    this.initializeData();
  }

  toggleButton2() {
    this.isButtonActive = true;
    localStorage.removeItem('isNavigatingBack');
    this.initializeData();
    this.getMainDepartments();
  }

  getDepartmentsCategoryData(deptId: number, companyId: number) {
    const storeGroup = localStorage.getItem('storeGroup');
    const selectedstoreGroupId = storeGroup ? parseInt(storeGroup, 10) : 0;
    this.masterService
      .getCategoriesByDeptUserID(deptId, companyId, true, selectedstoreGroupId)
      .subscribe((result: any) => {
        this.renderWomensDepartmentDat = result as SubDepartmentModel[];
        this.renderWomensDepartmentDataForOriginal = result as SubDepartmentModel[];
      });
  }

  getSelectedDepartmentData(values: any, urlName: any) {
    console.log(values);
    this.deptName = values.descriptionTranslated;
    localStorage.removeItem('CategoryDetails');
    localStorage.setItem('CategoryDetails', JSON.stringify(values));

    this.urlName = urlName;
    localStorage.setItem('deptID', JSON.stringify(values));
    localStorage.setItem('updatedFavoritesLists', JSON.stringify(this.combinedItemsList));

    if (values.categoryId == null || values.categoryId == 0) {
      const urlmaindept = '/production/home/sub-dept';
      this.route.navigateByUrl(urlmaindept);
    } else if (values.categoryId > 0) {
      localStorage.removeItem('CategoryData');
      localStorage.setItem('CategoryDetails', JSON.stringify(values));
      localStorage.setItem('ctgnamefromsubdept', JSON.stringify(values.categoryName));
      if (values.categoryId > 0 || values.categoryId > 0) {
        if (values.hasSubCategory && values.subCategoryId == 0) {
          this.route.navigateByUrl('/production/home/sub-categories');
        } else {
          this.route.navigateByUrl('/production/home/prod-item');
        }
        localStorage.setItem('deptID', JSON.stringify(values));
      }
    }
  }

  async getMainDepartments() {
    let storeGroup = localStorage.getItem('storeGroup');

    if (!storeGroup) {
      let store = localStorage.getItem('tempStoreId')!;
      if (!store) {
        store = localStorage.getItem('storeId')!;
      }
      const defaultStore = parseInt(store);
      let user = this.masterService.getUserDetailsFromStorage();
      await this.commonService.getStores(user.companyID).then(res => {
        const stores = res as Stores[];

        let store = stores.filter(item => item.storeId == defaultStore);
        localStorage.setItem('storeGroup', store[0]?.storeGroupId.toString() || '');
        storeGroup = store[0]?.storeGroupId.toString() || '';
      });
    }

    const selectedstoreGroupId = storeGroup ? parseInt(storeGroup) : 0;
    this.masterService.getDepartmentsProduction(true, selectedstoreGroupId).subscribe(
      (Response: any) => {
        this.mainDepartmentList = Response as MainDepartment[];
        setTimeout(() => {
          this.googleTranslateService.changeLanguage(this.selectedLanguage);
        }, 100);
      },
      (err: any) => {}
    );
  }

  getVoice(text: string) {
    let utteran = new SpeechSynthesisUtterance(text);
    speechSynthesis.speak(utteran);
  }

  fav(favItem: any) {
    if (this.updatingFavoriteDeptId === favItem.departmentId) return;
    this.updatingFavoriteDeptId = favItem.departmentId;

    const userDetails = this.masterService.getUserDetailsFromStorage();
    this.userFavoriteModal.userId = userDetails.userId;
    this.userFavoriteModal.departmentId = favItem.departmentId || favItem.departmentId;
    this.userFavoriteModal.companyId = userDetails.companyID;
    this.userFavoriteModal.favoriteID = favItem.favoriteId || favItem.favoriteID;

    if (favItem.favoriteId > 0) {
      this.userFavoriteModal.active = false;
    } else {
      if (
        favItem.favoriteID == null ||
        (favItem.favoriteID == 0 && (favItem.favoriteId == 0 || favItem.favoriteId == null))
      ) {
        this.userFavoriteModal.active = true;
      } else {
        this.userFavoriteModal.active = false;
      }
    }

    let data: UserFavoriteRequestModel = {
      userId: this.userFavoriteModal.userId,
      departmentId: this.userFavoriteModal.departmentId,
      categoryId: this.userFavoriteModal.categoryId,
      subCategoryId: this.userFavoriteModal.subCategoryId,
      companyId: this.userFavoriteModal.companyId,
      favoriteId: this.userFavoriteModal.favoriteID,
      active: this.userFavoriteModal.active,
      redirectLink: this.userFavoriteModal.redirectLink ? this.userFavoriteModal.redirectLink : '',
      createdDt: this.userFavoriteModal.createdDt ? this.userFavoriteModal.createdDt : new Date(),
      createdByUserId: this.userFavoriteModal.createdByUserId ? this.userFavoriteModal.createdByUserId : 0
    };

    if (this.userFavoriteModal.active === true) {
      this.favouritesState.setFlag(true);
    }

    this.masterService.addUpdateUserFavorite(data).subscribe({
      next: res => {
        this.initializeData();
        this.getAllFavItems();
        this.getMainDepartments();
      },
      error: error => {
        console.error('Error updating favorite:', error);
        this.toast.error('Failed to update favorite');
        this.updatingFavoriteDeptId = null;
      }
    });
  }

  getAllFavItems() {
    const userDetails = this.masterService.getUserDetailsFromStorage();
    this.masterService.getUserFavourites(userDetails.userId).subscribe(res => {
      this.combinedItemsList = res as UserFavoriteModal[];
      this.favQuantity = this.combinedItemsList.length;
      if (this.favQuantity < 10) {
        this.favQuantity = '0' + this.favQuantity.toString();
      } else {
        this.favQuantity = this.favQuantity;
      }
      this.favouritesState.setFlag(this.combinedItemsList.length > 0);
      if (this.combinedItemsList.length > 0) {
        this.isButtonActive = false;
        this.initializeData();
      } else {
        this.isButtonActive = true;
        this.initializeData();
        this.getMainDepartments();
      }
      this.updatingFavoriteDeptId = null;
      setTimeout(() => {
        this.googleTranslateService.changeLanguage(this.selectedLanguage);
      }, 100);
    });
  }

  async logOutUser() {
    const domain = environment.domainName;
    const companyweburl = environment.companyWebBaseUrl;
    onUserLogout();

    const performLogout = () => {
      document.cookie = `logoutEvent=1; domain=${domain}; path=/; max-age=10`;

      localStorage.removeItem('storeId');
      localStorage.removeItem('user');
      localStorage.removeItem('Items');
      localStorage.removeItem('ItemsLastUpdated');
      window.location.href = `${companyweburl}/shared/login`;
    };

    if (this.isLimitReached) {
      let user = this.masterService.getUserDetailsFromStorage();
      this.userInfoEmail.emailID = user.email;
      this.userInfoEmail.companyName = user.company;
      this.userInfoEmail.fullName = user.fullName;
      performLogout();
    } else {
      performLogout();
    }
  }
}
